'use client';

import React from 'react';

/**
 * SilkFlowBackground:
 * Ultra-subtle, organic moving silk sheen (3-4 micro-tones variation).
 * Emulates the gentle undulating waves of fine bed silk in a light breeze.
 * Completely GPU-accelerated and strictly restrained in contrast so text readability remains 100%.
 */
export const SilkFlowBackground: React.FC = () => {
  return (
    <div 
      className="fixed inset-0 pointer-events-none -z-10 overflow-hidden select-none"
      aria-hidden="true"
    >
      {/* Base warm archival linen tint */}
      <div className="absolute inset-0 bg-[#FAF8F5]" />

      {/* Layer 1: Primary undulating silk wave */}
      <div 
        className="absolute -inset-[30%] opacity-45 mix-blend-multiply blur-3xl animate-silk-primary"
        style={{
          background: 'radial-gradient(ellipse 70% 50% at 30% 20%, #F5EDE0 0%, transparent 65%), radial-gradient(ellipse 60% 60% at 75% 70%, #F3EAE0 0%, transparent 60%)',
          willChange: 'transform',
        }}
      />

      {/* Layer 2: Secondary cross-breeze silk wave (counter-movement) */}
      <div 
        className="absolute -inset-[30%] opacity-35 mix-blend-soft-light blur-3xl animate-silk-secondary"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 65% 30%, #FDFBF7 0%, transparent 70%), radial-gradient(ellipse 70% 70% at 20% 80%, #EDE4D8 0%, transparent 65%)',
          willChange: 'transform',
        }}
      />

      {/* Layer 3: Micro-tone champagne silk crest (subtle sheen) */}
      <div 
        className="absolute -inset-[20%] opacity-30 mix-blend-overlay blur-2xl animate-silk-tertiary"
        style={{
          background: 'radial-gradient(ellipse 50% 40% at 50% 50%, #FAF3E8 0%, transparent 75%)',
          willChange: 'transform',
        }}
      />

      {/* Very faint architectural 1px grid watermark (Swiss precision) */}
      <div 
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: `
            linear-gradient(to right, #141416 1px, transparent 1px),
            linear-gradient(to bottom, #141416 1px, transparent 1px)
          `,
          backgroundSize: '120px 120px',
        }}
      />
    </div>
  );
};
